import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { createPublicApi } from '../services/api';
import MenuItemCard from '../components/menu/MenuItemCard';
import { SkeletonCard } from '../components/common/Skeleton';
import { useDebounce } from '../hooks';

const ITEMS_PER_PAGE = 10;

const CAT_ICONS = {
  'Coffee':'☕','Tea':'🍵','Snacks':'🍟','Desserts':'🍰',
  'Drinks':'🥤','Breakfast':'🍳','Pizza':'🍕','Burger':'🍔',
  'Pasta':'🍝','Sandwich':'🥪','Salad':'🥗','Soup':'🍲',
  'softdrink':'🧃','Sweets':'🍬','Main Course':'🍛','Starters':'🥗',
};

const Menu = () => {
  const { slug } = useParams();
  const [allItems,       setAllItems]       = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search,         setSearch]         = useState('');
  const [loading,        setLoading]        = useState(true);
  const [visibleCount,   setVisibleCount]   = useState(ITEMS_PER_PAGE);
  const [loadingMore,    setLoadingMore]    = useState(false);
  const [isMobile,       setIsMobile]       = useState(window.innerWidth < 768);
  const [sortBy,   setSortBy]   = useState('default');
  const [showLiked, setShowLiked] = useState(false);
  const likesKey = `cafe_liked_items_${slug || 'default'}`;
  const [likedIds,  setLikedIds]  = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cafe_liked_items_${slug || 'default'}`) || '[]'); }
    catch { return []; }
  }); // default | rating | price_asc | price_desc
  const loaderRef = useRef(null);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const syncLikes = (e) => {
      // Only sync if event is for this restaurant
      if (e.detail?.slug && e.detail.slug !== slug) return;
      try { setLikedIds(JSON.parse(localStorage.getItem(likesKey) || '[]')); }
      catch { setLikedIds([]); }
    };
    window.addEventListener('likes-updated', syncLikes);
    return () => window.removeEventListener('likes-updated', syncLikes);
  }, [slug, likesKey]);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const fetchMenu = () => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    setLoading(true);
    pubApi.menu.getCategories().then(r => setCategories(r.data.data || [])).catch(() => {});
    pubApi.menu.getAll({ limit: 200 }).then(r => {
      setAllItems(r.data.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchMenu(); }, [slug]);

  // Reset visible count when filter/search changes
  useEffect(() => { setVisibleCount(ITEMS_PER_PAGE); }, [activeCategory, debouncedSearch, sortBy, showLiked]);

  const filteredItems = allItems.filter(m => {
    if (!m.is_available) return false;
    if (activeCategory && m.category_name !== activeCategory) return false;
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      return m.name.toLowerCase().includes(q) || (m.description||'').toLowerCase().includes(q);
    }
    return true;
  });

  // Sort
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortBy === 'rating')     return (parseFloat(b.avg_rating)||0) - (parseFloat(a.avg_rating)||0);
    if (sortBy === 'price_asc')  return parseFloat(a.price) - parseFloat(b.price);
    if (sortBy === 'price_desc') return parseFloat(b.price) - parseFloat(a.price);
    return 0;
  });
  const unavailable   = allItems.filter(m => !m.is_available);
  const likedItems    = allItems.filter(m => m.is_available && likedIds.includes(m.id));
  const displayItems  = showLiked ? likedItems : sortedItems;
  const visibleItems  = displayItems.slice(0, visibleCount);
  const hasMore       = visibleCount < displayItems.length;

  // Infinite scroll using IntersectionObserver
  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore && !loadingMore) {
      setLoadingMore(true);
      setTimeout(() => {
        setVisibleCount(prev => prev + ITEMS_PER_PAGE);
        setLoadingMore(false);
      }, 400);
    }
  }, [hasMore, loadingMore, showLiked]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const getCatCount = (name) => allItems.filter(m => m.is_available && m.category_name === name).length;
  const clearAll    = () => { setSearch(''); setActiveCategory(''); setShowLiked(false); };

  const allTabs = [
    { key:'', label:'All', icon:'🍽️', count: allItems.filter(m=>m.is_available).length },
    ...categories.map(c => ({ key:c.name, label:c.name, icon: CAT_ICONS[c.name]||'🍴', count: getCatCount(c.name) })),
  ];

  const gridStyle = {
    display:'grid',
    gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(220px,1fr))',
    gap: isMobile ? 12 : 20,
    alignItems:'start',
  };

  return (
    <div style={{ minHeight:'100vh', background:'#fdf8f3' }}>

      {/* ── Header ── */}
      <div style={{ background:'#1c0f04', paddingTop: isMobile ? 68 : 76 }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding: isMobile ? '0 16px' : '0 32px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, padding: isMobile?'18px 0 16px':'24px 0 20px', flexWrap: isMobile?'wrap':'nowrap' }}>
            <div style={{ flexShrink:0 }}>
              <h1 style={{ fontFamily:'"Playfair Display",serif', fontSize: isMobile?22:28, fontWeight:800, color:'#f5ede0', margin:'0 0 3px', letterSpacing:'-0.3px' }}>
                Our Menu
              </h1>
              <p style={{ color:'#6b4226', fontSize:12, margin:0, fontWeight:500 }}>
                {allItems.filter(m=>m.is_available).length} items · {categories.length} categories
              </p>
            </div>
            <div style={{ position:'relative', width: isMobile?'100%':300, flexShrink:0 }}>
              <span style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', fontSize:15, pointerEvents:'none', color:'#6b4226' }}>🔍</span>
              <input type="text" placeholder="Search items..." value={search}
                onChange={e => { setSearch(e.target.value); setActiveCategory(''); }}
                style={{ width:'100%', padding:'10px 38px 10px 40px', borderRadius:12, fontSize:13,
                  border:`1.5px solid ${search?'#c96428':'rgba(201,100,40,0.2)'}`,
                  background:'rgba(255,255,255,0.07)', color:'#f5ede0',
                  fontFamily:'inherit', outline:'none', boxSizing:'border-box', transition:'all 0.2s',
                  boxShadow: search?'0 0 0 3px rgba(201,100,40,0.12)':'none',
                }}/>
              {search && (
                <button onClick={()=>setSearch('')}
                  style={{ position:'absolute', right:11, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'rgba(245,237,224,0.5)', fontSize:15, lineHeight:1, padding:2 }}>✕</button>
              )}
            </div>
          </div>
        </div>
        <style>{`input::placeholder { color: rgba(107,66,38,0.6) !important; } div::-webkit-scrollbar { display: none; }`}</style>
      </div>

      {/* ── Sticky Category Tabs ── */}
      <div style={{ position:'sticky', top: isMobile ? 64 : 80, zIndex:40, background:'#ffffff', borderBottom:'1px solid #e8e0d8', boxShadow:'0 2px 12px rgba(28,21,18,0.07)' }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding: isMobile?'10px 16px':'10px 32px' }}>
          <div style={{ display:'flex', overflowX:'auto', scrollbarWidth:'none', msOverflowStyle:'none', gap:8, alignItems:'center' }}>
            {/* Favourites tab */}
            <button onClick={() => { setShowLiked(!showLiked); setActiveCategory(''); setSearch(''); }}
              style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0,
                padding: isMobile?'6px 14px':'7px 16px',
                background: showLiked ? '#ef4444' : 'transparent',
                border: `1.5px solid ${showLiked ? '#ef4444' : '#e8e0d8'}`,
                borderRadius:99, color: showLiked ? '#fff' : '#7a6355',
                fontWeight: showLiked ? 600 : 500, fontSize: isMobile?12:13,
                cursor:'pointer', fontFamily:'inherit', transition:'all 0.18s', whiteSpace:'nowrap',
              }}>
              <span style={{ fontSize:14 }}>{showLiked ? '❤️' : '🤍'}</span>
              <span>Favourites</span>
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:99,
                background: showLiked ? 'rgba(255,255,255,0.25)' : '#f0ebe4',
                color: showLiked ? 'white' : '#a89080', marginLeft:2,
              }}>{likedIds.length}</span>
            </button>
            <div style={{ width:1, height:20, background:'#e8e0d8', flexShrink:0 }}/>
            {allTabs.map(tab => {
              const active = activeCategory === tab.key && !search;
              return (
                <button key={tab.key} onClick={() => { setActiveCategory(tab.key); setSearch(''); }}
                  style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0,
                    padding: isMobile?'6px 14px':'7px 16px',
                    background: active ? '#b85c38' : 'transparent',
                    border: `1.5px solid ${active ? '#b85c38' : '#e8e0d8'}`,
                    borderRadius:99, color: active ? '#ffffff' : '#7a6355',
                    fontWeight: active ? 600 : 500, fontSize: isMobile ? 12 : 13,
                    cursor:'pointer', fontFamily:'inherit', transition:'all 0.18s', whiteSpace:'nowrap',
                  }}>
                  <span style={{ fontSize: isMobile?13:14 }}>{tab.icon}</span>
                  <span>{tab.label}</span>
                  <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:99,
                    background: active ? 'rgba(255,255,255,0.25)' : '#f0ebe4',
                    color: active ? 'white' : '#a89080', marginLeft:2,
                  }}>{tab.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth:1280, margin:'0 auto', padding: isMobile?'20px 12px 60px':'28px 32px 60px' }}>


        {/* ── Sort Bar ── */}
        {!debouncedSearch && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
            {[
              { key:'default',    label:'Default' },
              { key:'rating',     label:'⭐ Top Rated' },
              { key:'price_asc',  label:'₹ Low → High' },
              { key:'price_desc', label:'₹ High → Low' },
            ].map(s => (
              <button key={s.key} onClick={() => setSortBy(s.key)}
                style={{
                  padding:'6px 14px', borderRadius:99, fontSize:12, fontWeight:600,
                  border:`1.5px solid ${sortBy===s.key ? '#c96428' : '#e8e0d8'}`,
                  background: sortBy===s.key ? '#c96428' : '#fff',
                  color: sortBy===s.key ? '#fff' : '#7a6355',
                  cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
                }}>
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* ── All Items heading ── */}
        {!activeCategory && !debouncedSearch && (
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <div style={{ width:4, height:24, borderRadius:99, background:'linear-gradient(180deg,#c96428,#9a4a1e)' }}/>
            <h2 style={{ fontFamily:'"Playfair Display",serif', fontSize: isMobile?17:20, fontWeight:800, color:'#1a0f00', margin:0 }}>
              All Items
            </h2>
          </div>
        )}

        {/* ── Items Grid ── */}
        <AnimatePresence mode="wait">
          <motion.div key={activeCategory + debouncedSearch}
            initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}}
            transition={{duration:0.18}}>

            {loading ? (
              <div style={gridStyle}>
                {Array(10).fill(0).map((_,i) => <SkeletonCard key={i}/>)}
              </div>
            ) : visibleItems.length > 0 ? (
              <>
                <div style={gridStyle}>
                  {visibleItems.map((item, i) => (
                    <motion.div key={item.id}
                      initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}
                      transition={{delay: Math.min(i * 0.03, 0.3)}}
                      style={{ height:'100%' }}>
                      <MenuItemCard item={item}/>
                    </motion.div>
                  ))}
                </div>

                {/* Counter */}
                <p style={{ textAlign:'center', fontSize:12, color:'#a07858', margin:'20px 0 0' }}>
                  Showing {visibleItems.length} of {filteredItems.length} items
                </p>

                {/* Infinite scroll trigger */}
                <div ref={loaderRef} style={{ height:40, display:'flex', alignItems:'center', justifyContent:'center', marginTop:8 }}>
                  {loadingMore && (
                    <div style={{ display:'flex', gap:8 }}>
                      {[0,1,2].map(i => (
                        <motion.div key={i}
                          animate={{ scale:[1,1.4,1], opacity:[0.4,1,0.4] }}
                          transition={{ repeat:Infinity, duration:0.9, delay:i*0.2 }}
                          style={{ width:8, height:8, borderRadius:'50%', background:'#c96428' }}/>
                      ))}
                    </div>
                  )}
                  {!hasMore && displayItems.length > ITEMS_PER_PAGE && (
                    <p style={{ fontSize:12, color:'#c0a080' }}>You've seen all items ✓</p>
                  )}
                </div>
              </>
            ) : (
              <div style={{ textAlign:'center', padding:'80px 24px' }}>
                <div style={{ fontSize:52, marginBottom:16 }}>{showLiked ? '🤍' : debouncedSearch?'🔍':'🍽️'}</div>
                <h3 style={{ fontSize:19, fontWeight:800, color:'#1a0f00', margin:'0 0 8px', fontFamily:'"Playfair Display",serif' }}>
                  {showLiked ? 'No favourites yet' : debouncedSearch?'No results found':'No items available'}
                </h3>
                <p style={{ color:'#a07858', fontSize:14, margin:'0 0 24px' }}>
                  {showLiked ? 'Tap ❤️ on any item to save it here' : debouncedSearch?`Nothing matches "${debouncedSearch}"`:'Check back soon!'}
                </p>
                {(debouncedSearch||activeCategory) && (
                  <button onClick={clearAll}
                    style={{ padding:'11px 26px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#c96428,#9a4a1e)', color:'white', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 14px rgba(201,100,40,0.3)' }}>
                    Show All Items
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Unavailable items */}
        {!debouncedSearch && !activeCategory && unavailable.length > 0 && (
          <div style={{ marginTop:48, paddingTop:28, borderTop:'1px dashed #edd9c8' }}>
            <p style={{ fontSize:11, fontWeight:700, color:'#c0a080', textTransform:'uppercase', letterSpacing:'1.2px', marginBottom:16 }}>
              Currently Unavailable ({unavailable.length})
            </p>
            <div style={{ ...gridStyle, opacity:0.45, pointerEvents:'none' }}>
              {unavailable.map(item => <MenuItemCard key={item.id} item={item}/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
